import * as dayjs from 'dayjs';
import { Duration } from 'dayjs/plugin/duration.js';
import { ClientNoteComponent } from '../../components/notes/client-note-component.js';
import { NoteComponent } from '../../components/notes/note-component.js';
import { IEntity } from '../../components/core/entity.js';
import { Component } from '../../components/core/component.js';
import { ComponentList } from '../../components/core/component-list.js';
import { TimeZone } from '../../time/timezone.js';
import { MapUtils } from '../../utility/map-utils.js';
import { CalendarEntryStatus } from '../calendar-entry.js';
import { TimeZoneRegion } from './time-zone-region.js';
import { VersionRange } from '../core/version-range.js';
import { DataModel, DataModelBuilder, isSameData,
  isSameList, SerializationContext, SerializedData } from '../dto/dto.js';
import { CustomerProfile } from './customer-profile.js';
import { ContactDetails } from './contact-details.js';
import { DayOfYear } from './day-of-year.js';
import { OnlinePaymentDetails } from './online-payment-details.js';
import { CalendarOrderItem } from './calendar-order-item.js';
import { PaymentType } from './payment-type.js';
import { StaffProfile } from './staff-profile.js';
import { TimeOfDay } from './time-of-day.js';
import { BusinessModuleRef } from './business-module-ref.js';
import { CalendarOrderBase } from '../calendar-order-base.js';
import { fromEnumAsString, fromEnumAsStringOrNull, toEnumFromString } from '../../serialization/enum-utils.js';
import { QueryableOptionalString } from '../core/optional.js';
import { MonthOfYear } from './month-of-year.js';
import { PaymentStatus } from './payment-status.js';
import { Language } from './language.js';
import { getDefaultPaymentMethodForPaymentType, PaymentMethod } from './payment-method.js';
import { CustomerAccountBase } from './customer-account-base.js';
import { TrueTime } from '../../time/true-time.js';
import { Country } from './country.js';
import { OrderSource } from './order-source.js';
import { OrderDomain } from './order-domain.js';

export class OnlineCalendarOrder extends CalendarOrderBase implements IEntity {
  static readonly TYPE_ID: string = '451e4ff39c3245bbb922cd83f4abd047';
  static readonly SCHEMA_VERSION: number = 10;

  private readonly totalDuration: Duration;
  public readonly dayId: string;
  public readonly monthId: string;
  public readonly userId: string | null;
  public readonly country: Country;
  public readonly language: Language | null;
  public readonly customerId: string | null;

  public readonly source: OrderSource = OrderSource.Client;
  public readonly domain: OrderDomain = OrderDomain.inTime;

  private constructor(
    public readonly uniqueId: string,
    public readonly creatorId: string,
    public readonly businessRef: BusinessModuleRef,
    public readonly dayOfYear: DayOfYear,
    public readonly timezone: TimeZoneRegion,
    public readonly creationDate: dayjs.Dayjs,
    public readonly client: CustomerProfile,
    public readonly clientId: string,
    public readonly contactDetails: ContactDetails,
    public readonly timeOfDay: TimeOfDay,
    public readonly staffMemberId: QueryableOptionalString,
    public readonly staffMember: StaffProfile | null,
    public readonly status: CalendarEntryStatus,
    public readonly services: CalendarOrderItem[],
    public readonly hasBeenReviewed: boolean,
    public readonly paymentType: PaymentType,
    public readonly paymentMethod: PaymentMethod,
    public readonly onlinePaymentDetails: OnlinePaymentDetails | null,
    public readonly components: ComponentList,
    public readonly groupId: string | null,
    public readonly parentId: QueryableOptionalString,
    public readonly via: BusinessModuleRef | null,
    public readonly cancelledDate: dayjs.Dayjs | null,
    public readonly cancelledBy: string | null,
  ) {
    super();

    this.totalDuration = CalendarOrderBase.calculateTotalDuration(services);
    this.dayId = dayOfYear.uniqueId;
    this.monthId = MonthOfYear.dayOfYearToUniqueId(dayOfYear);
    this.userId = client.userId;
    this.customerId = client.uniqueId;
    this.language = client.language;
    this.country = contactDetails.country;
  }

  get duration(): Duration {
    return this.totalDuration;
  }

  get $typeId(): string {
    return OnlineCalendarOrder.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(6, OnlineCalendarOrder.SCHEMA_VERSION);
  }

  static fromMap(context: SerializationContext, map: SerializedData): OnlineCalendarOrder {
    return new OnlineCalendarOrder(
        MapUtils.get<string>(map, 'uniqueId'),
        MapUtils.get<string>(map, 'creatorId'),
        BusinessModuleRef.mustDecode(MapUtils.get<string>(map, 'businessRef')),
        context.fromData<DayOfYear>(MapUtils.getRaw(map, 'dayOfYear') as SerializedData),
        toEnumFromString(MapUtils.get<string>(map, 'timezone'), TimeZoneRegion),
        context.decodeDate(MapUtils.get<number>(map, 'creationDate')),
        context.fromData<CustomerProfile>(MapUtils.getRaw(map, 'client') as SerializedData),
        MapUtils.get<string>(map, 'clientId'),
        context.fromData<ContactDetails>(MapUtils.getRaw(map, 'contactDetails') as SerializedData),
        context.fromData<TimeOfDay>(MapUtils.getRaw(map, 'timeOfDay') as SerializedData),
        new QueryableOptionalString(MapUtils.getOrNull<string>(map, 'staffMemberId')),
        context.fromDataOrNull<StaffProfile>(MapUtils.getOrNull<SerializedData>(map, 'staffMember')),
        MapUtils.get<CalendarEntryStatus>(map, '$status'),
        MapUtils.getList<CalendarOrderItem>(
            map, 'services',
            (value) => context.fromData<CalendarOrderItem>(value as SerializedData)
        ),
        MapUtils.get<boolean>(map, 'hasBeenReviewed'),
        toEnumFromString(MapUtils.get<string>(map, 'paymentType'), PaymentType),
        toEnumFromString(MapUtils.get<string>(map, 'paymentMethod'), PaymentMethod),
        context.fromDataOrNull<OnlinePaymentDetails>(MapUtils.getOrNull<SerializedData>(map, 'onlinePaymentDetails')),
        ComponentList.deserialize(
            context, MapUtils.getList<SerializedData>(map, 'components', (data) => data as SerializedData),
        ),
        MapUtils.getOrNull<string>(map, 'groupId'),
        new QueryableOptionalString(MapUtils.getOrNull<string>(map, 'parentId')),
        BusinessModuleRef.decode(MapUtils.getOrNull<string>(map, 'via')),
        context.decodeDateOrNull(MapUtils.getOrNull<number>(map, 'cancelledDate')),
        MapUtils.getOrNull<string>(map, 'cancelledBy'),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'creatorId': this.creatorId,
      'userId': this.userId,
      'businessRef': this.businessRef.encode(),
      'dayId': this.dayId,
      'monthId': this.monthId,
      'dayOfYear': context.toData(this.dayOfYear),
      'timeOfDay': context.toData(this.timeOfDay),
      'timezone': fromEnumAsString(this.timezone),
      'date': context.encodeDate(this.time),
      'creationDate': context.encodeDate(this.creationDate),
      'contactDetails': context.toData(this.contactDetails),
      'clientId': this.clientId,
      'staffMemberId': this.staffMemberId.queryableValue,
      'client': context.toData(this.client),
      'staffMember': context.toDataOrNull(this.staffMember),
      'services': this.services.map((e) => context.toData(e)),
      'hasBeenReviewed': this.hasBeenReviewed,
      'components': this.components.serialize(context),
      'paymentType': fromEnumAsString(this.paymentType),
      'paymentMethod': fromEnumAsString(this.paymentMethod),
      'onlinePaymentDetails': context.toDataOrNull(this.onlinePaymentDetails),
      '$status': this.status,
      'groupId': this.groupId,
      'parentId': this.parentId.queryableValue,
      'via': this.via?.encode() ?? null,
      'cancelledDate': context.encodeDateOrNull(this.cancelledDate),
      'cancelledBy': this.cancelledBy,
    };
  }

  clone(): this {
    return new OnlineCalendarOrder(
        this.uniqueId,
        this.creatorId,
        this.businessRef.clone(),
        this.dayOfYear.clone(),
        this.timezone,
        TimeZone.cloneDate(this.creationDate),
        this.client.clone(),
        this.clientId,
        this.contactDetails.clone(),
        this.timeOfDay.clone(),
        this.staffMemberId.clone(),
        this.staffMember?.clone() ?? null,
        this.status,
        this.services.map((e) => e.clone()),
        this.hasBeenReviewed,
        this.paymentType,
        this.paymentMethod,
        this.onlinePaymentDetails?.clone() ?? null,
        this.components.clone(),
        this.groupId,
        this.parentId.clone(),
        this.via?.clone() ?? null,
        TimeZone.cloneDateOrNull(this.cancelledDate),
        this.cancelledBy,
    ) as this;
  }

  copyAsAborted(): this {
    return new OnlineCalendarOrder(
        this.uniqueId,
        this.creatorId,
        this.businessRef.clone(),
        this.dayOfYear.clone(),
        this.timezone,
        TimeZone.cloneDate(this.creationDate),
        this.client.clone(),
        this.clientId,
        this.contactDetails.clone(),
        this.timeOfDay.clone(),
        this.staffMemberId.clone(),
        this.staffMember?.clone() ?? null,
        CalendarEntryStatus.Aborted,
        this.services.map((e) => e.clone()),
        this.hasBeenReviewed,
        this.paymentType,
        this.paymentMethod,
        this.onlinePaymentDetails?.clone() ?? null,
        this.components.clone(),
        this.groupId,
        this.parentId.clone(),
        this.via?.clone() ?? null,
        TimeZone.cloneDateOrNull(this.cancelledDate),
        this.cancelledBy,
    ) as this;
  }

  copyAsCanceled(cancelledBy: string | null): this {
    return new OnlineCalendarOrder(
        this.uniqueId,
        this.creatorId,
        this.businessRef.clone(),
        this.dayOfYear.clone(),
        this.timezone,
        TimeZone.cloneDate(this.creationDate),
        this.client.clone(),
        this.clientId,
        this.contactDetails.clone(),
        this.timeOfDay.clone(),
        this.staffMemberId.clone(),
        this.staffMember?.clone() ?? null,
        CalendarEntryStatus.Canceled,
        this.services.map((e) => e.clone()),
        this.hasBeenReviewed,
        this.paymentType,
        this.paymentMethod,
        this.onlinePaymentDetails?.clone() ?? null,
        this.components.clone(),
        this.groupId,
        this.parentId.clone(),
        this.via?.clone() ?? null,
        TrueTime.now(),
        cancelledBy,
    ) as this;
  }

  copyWithPaymentDetails(paymentDetails: OnlinePaymentDetails): this {
    return new OnlineCalendarOrder(
        this.uniqueId,
        this.creatorId,
        this.businessRef.clone(),
        this.dayOfYear.clone(),
        this.timezone,
        TimeZone.cloneDate(this.creationDate),
        this.client.clone(),
        this.clientId,
        this.contactDetails.clone(),
        this.timeOfDay.clone(),
        this.staffMemberId.clone(),
        this.staffMember?.clone() ?? null,
        this.status,
        this.services.map((e) => e.clone()),
        this.hasBeenReviewed,
        this.paymentType,
        this.paymentMethod,
        paymentDetails,
        this.components.clone(),
        this.groupId,
        this.parentId.clone(),
        this.via?.clone() ?? null,
        TimeZone.cloneDateOrNull(this.cancelledDate),
        this.cancelledBy,
    ) as this;
  }

  copyWithPaymentStatus(status: PaymentStatus): this {
    return new OnlineCalendarOrder(
        this.uniqueId,
        this.creatorId,
        this.businessRef.clone(),
        this.dayOfYear.clone(),
        this.timezone,
        TimeZone.cloneDate(this.creationDate),
        this.client.clone(),
        this.clientId,
        this.contactDetails.clone(),
        this.timeOfDay.clone(),
        this.staffMemberId.clone(),
        this.staffMember?.clone() ?? null,
        this.status,
        this.services.map((e) => e.clone()),
        this.hasBeenReviewed,
        this.paymentType,
        this.paymentMethod,
        this.onlinePaymentDetails?.copyWith({ status }) ?? null,
        this.components.clone(),
        this.groupId,
        this.parentId.clone(),
        this.via?.clone() ?? null,
        TimeZone.cloneDateOrNull(this.cancelledDate),
        this.cancelledBy,
    ) as this;
  }

  copyWithPaymentMethod(paymentMethod: PaymentMethod): this {
    return new OnlineCalendarOrder(
        this.uniqueId,
        this.creatorId,
        this.businessRef.clone(),
        this.dayOfYear.clone(),
        this.timezone,
        TimeZone.cloneDate(this.creationDate),
        this.client.clone(),
        this.clientId,
        this.contactDetails.clone(),
        this.timeOfDay.clone(),
        this.staffMemberId.clone(),
        this.staffMember?.clone() ?? null,
        this.status,
        this.services.map((e) => e.clone()),
        this.hasBeenReviewed,
        this.paymentType,
        paymentMethod,
        this.onlinePaymentDetails?.clone() ?? null,
        this.components.clone(),
        this.groupId,
        this.parentId.clone(),
        this.via?.clone() ?? null,
        TimeZone.cloneDateOrNull(this.cancelledDate),
        this.cancelledBy,
    ) as this;
  }

  copyWithBusinessRef(businessRef: BusinessModuleRef): this {
    return new OnlineCalendarOrder(
        this.uniqueId,
        this.creatorId,
        businessRef.clone(),
        this.dayOfYear.clone(),
        this.timezone,
        TimeZone.cloneDate(this.creationDate),
        this.client.copyWithBusinessRef(businessRef),
        this.clientId,
        this.contactDetails.clone(),
        this.timeOfDay.clone(),
        this.staffMemberId.clone(),
        this.staffMember?.copyWithBusinessRef(businessRef) ?? null,
        this.status,
        this.services.map((e) => e.copyWithBusinessRef(businessRef)),
        this.hasBeenReviewed,
        this.paymentType,
        this.paymentMethod,
        this.onlinePaymentDetails?.clone() ?? null,
        this.components.copyWithBusinessRef(businessRef),
        this.groupId,
        this.parentId.clone(),
        this.via?.clone() ?? null,
        TimeZone.cloneDateOrNull(this.cancelledDate),
        this.cancelledBy,
    ) as this;
  }

  assignToCustomerAccount(account: CustomerAccountBase): this {
    return new OnlineCalendarOrder(
        this.uniqueId,
        account.uniqueId,
        this.businessRef.clone(),
        this.dayOfYear.clone(),
        this.timezone,
        TimeZone.cloneDate(this.creationDate),
        this.client.assignToCustomerAccount(account),
        this.clientId,
        this.contactDetails.clone(),
        this.timeOfDay.clone(),
        this.staffMemberId.clone(),
        this.staffMember?.clone() ?? null,
        this.status,
        this.services.map((e) => e.clone()),
        this.hasBeenReviewed,
        this.paymentType,
        this.paymentMethod,
        this.onlinePaymentDetails?.clone() ?? null,
        this.components.clone(),
        this.groupId,
        this.parentId.clone(),
        this.via?.clone() ?? null,
        TimeZone.cloneDateOrNull(this.cancelledDate),
        this.cancelledBy,
    ) as this;
  }

  isSame(other: OnlineCalendarOrder): boolean {
    if(this === other) {
      return true;
    }

    return isSameData(other.client, this.client) &&
        isSameData(other.dayOfYear, this.dayOfYear) &&
        isSameData(other.timeOfDay, this.timeOfDay) &&
        isSameData(other.staffMember, this.staffMember) &&
        isSameData(other.contactDetails, this.contactDetails) &&
        isSameData(other.onlinePaymentDetails, this.onlinePaymentDetails) &&
        isSameList(other.services, this.services, isSameData) &&
        other.components.isSame(this.components) &&
        other.timezone === this.timezone &&
        other.uniqueId === this.uniqueId &&
        other.creatorId === this.creatorId &&
        other.cancelledDate === this.cancelledDate &&
        other.cancelledBy === this.cancelledBy &&
        isSameData(other.businessRef, this.businessRef) &&
        isSameData(other.staffMemberId, this.staffMemberId) &&
        other.clientId === this.clientId &&
        other.creationDate === this.creationDate &&
        other.hasBeenReviewed === this.hasBeenReviewed &&
        other.status === this.status &&
        other.paymentType === this.paymentType &&
        other.paymentMethod === this.paymentMethod &&
        other.groupId === this.groupId &&
        isSameData(other.parentId, this.parentId) &&
        isSameData(other.via, this.via);
  }

  tryGetCustomer(): CustomerProfile | null {
    return this.client;
  }

  hasComponentByTypeId(typeId: string): boolean {
    return this.components.hasComponentByTypeId(typeId);
  }

  mustFindComponentByTypeId<C extends Component>(typeId: string): C {
    return this.components.mustFindComponentByTypeId<C>(typeId);
  }

  findComponentByTypeId<C extends Component>(typeId: string): C | null {
    return this.components.findComponentByTypeId<C>(typeId);
  }

  findComponentByInstanceId<C extends Component>(instanceId: string): C | null {
    return this.components.findComponentByInstanceId<C>(instanceId);
  }

  findComponentsByTypeId<C extends Component>(typeId: string): C[] {
    return this.components.findComponentsByTypeId<C>(typeId);
  }

  addComponent(component: Component): void {
    this.components.addComponent(component);
  }

  addComponents(list: Component[]): void {
    this.components.addComponents(list);
  }

  removeComponentByTypeId(typeId: string): boolean {
    return this.components.removeComponentByTypeId(typeId);
  }

  removeComponentByInstanceId(instanceId: string): boolean {
    return this.components.removeComponentByInstanceId(instanceId);
  }

  removeComponentAt(index: number): void {
    this.components.removeComponentAt(index);
  }

  removeAllComponents(): void {
    this.components.removeAllComponents();
  }

  popAllComponentsByTypeId<C extends Component>(typeId: string): C[] {
    return this.components.popAllComponentsByTypeId(typeId);
  }

  popComponentByTypeId<C extends Component>(typeId: string): C | null {
    return this.components.popComponentByTypeId(typeId);
  }

  popComponent<C extends Component>(component: C): C | null {
    return this.components.popComponent(component);
  }

  replaceComponent<C extends Component>(replacement: C): boolean {
    return this.components.replaceComponent(replacement);
  }

  addOrReplaceComponent<C extends Component>(component: C): void {
    this.components.addOrReplaceComponent(component);
  }

  mustFindComponentField<C extends Component, F>(componentTypeId: string, find: (c: C) => F): F {
    return this.components.mustFindComponentField<C, F>(componentTypeId, find);
  }

  findComponentField<C extends Component, F>(componentTypeId: string, find: (c: C) => F): F | null {
    return this.components.findComponentField<C, F>(componentTypeId, find);
  }

  static verifyType(order: DataModel): boolean {
    return order instanceof OnlineCalendarOrder;
  }

  static assertType(order: DataModel): order is OnlineCalendarOrder {
    return order instanceof OnlineCalendarOrder;
  }
}

export class OnlineCalendarOrderBuilder extends DataModelBuilder<OnlineCalendarOrder> {
  build(context: SerializationContext, map: SerializedData): OnlineCalendarOrder {
    return OnlineCalendarOrder.fromMap(context, map);
  }

  optimizeForQuery(context: SerializationContext, model: OnlineCalendarOrder): SerializedData | null {
    let paymentStatus: PaymentStatus | null = null;

    if(model.paymentType === PaymentType.Online) {
      paymentStatus = model.onlinePaymentDetails?.status ?? null;
    }
    else {
      paymentStatus = PaymentStatus.Succeeded;
    }

    return {
      'paymentStatus': fromEnumAsStringOrNull(paymentStatus),
      'paymentType': fromEnumAsString(model.paymentType),
      'paymentMethod': fromEnumAsString(model.paymentMethod),
    };
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);

    this.migrateEnum(map, 'paymentType', PaymentType, PaymentType.OnDelivery);
    this.migrateEnum(map, 'paymentMethod', PaymentMethod, PaymentMethod.Cash);
    this.migrateEnum(map, '$status', CalendarEntryStatus, CalendarEntryStatus.Open);

    MapUtils.replace(
        map, '$status', 'DELETED',
        fromEnumAsString(CalendarEntryStatus.Canceled),
    );

    // Needed for deserializing invoices with a graceful creation date! See builder of [OnlinePaymentDetails].
    const creationDate = context.decodeDate(MapUtils.getInteger(map, 'creationDate'));
    context.storage.set('orderCreationDate', creationDate);

    if(versionRange.contains(OnlineCalendarOrder.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(OnlineCalendarOrder.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    if(version < 2) {
      MapUtils.setIfNullOrUndefined(map, 'hasBeenReviewed', false);
      MapUtils.set(map, 'components', []);
      version = 2;
    }

    if(version < 3) {
      const components = ComponentList.deserialize(
          context, MapUtils.getList<SerializedData>(map, 'components', (data) => data as SerializedData),
      );
      const noteComponent = components.findComponentByTypeId<NoteComponent>(NoteComponent.TYPE_ID);

      if(noteComponent) {
        components.addComponent(new ClientNoteComponent(noteComponent.note));
      }

      MapUtils.set(map, 'components', components.serialize(context));
      version = 3;
    }

    if(version < 4) {
      MapUtils.set(map, 'groupId', null);
      MapUtils.set(map, 'parentId', null);
      version = 4;
    }

    if(version < 5) {
      MapUtils.set(map, 'onlinePaymentDetails', null);
      version = 5;
    }

    // In this version [businessId] was replaced with [businessRef]
    if(version < 6) {
      MapUtils.setIfNullOrUndefined(map, 'businessRef', MapUtils.get(map, 'businessId'));
      version = 6;
    }

    // In this version the [dayId] field was removed from the constructor
    if(version < 7) {
      version = 7;
    }

    // In this version parameter `via` of type `Optional<BusinessModuleRef>` was introduced.
    if(version < 8) {
      version = 8;
    }

    // In this version parameter `paymentMethod` of type `PaymentMethod` was
    // introduced.
    if(version < 9) {
      version = 9;

      const paymentType: PaymentType = toEnumFromString(MapUtils.get<string>(map, 'paymentType'), PaymentType);
      map['paymentMethod'] = fromEnumAsString(getDefaultPaymentMethodForPaymentType(paymentType));
    }

    // In this version the [cancelledDate] and [cancelledBy] fields were added
    if(version < 10) {
      version = 10;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
